import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { flatMap, isUndefined, join, map, uniq } from 'es-toolkit/compat';
import React, { useMemo } from 'react';
import { useClipboard } from 'use-clipboard-copy';
import YAML from 'yaml';

import { AnnotationAssignment, TodoOption } from 'interfaces/annotation';
import { ClassConfigYamlSchema, Model } from 'interfaces/model';
import moment from 'moment';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { ClassConfigYamlPreview } from './ClassConfigYamlPreview';
import { generateClassConfigFromForm, useFilterAndFlattenAnnotationAssignments } from './helpers';
import { IFormValues } from './types';

interface Props {
  studyId?: string;
  formMethods: UseFormReturn<Partial<IFormValues>, any>;
  modelDetailsResponse?: Model;
  annotationAssignments: AnnotationAssignment[];
}

export const PreviewTrainingPlanStep: React.FC<Props> = ({
  studyId,
  formMethods,
  annotationAssignments,
  modelDetailsResponse,
}) => {
  const { watch } = formMethods;
  const stainId = watch('stainId');
  const selectedSlideIds = watch('slideIds') || [];
  const selectedAssignmentIds = watch('annotationAssignmentIds') || [];
  const modelType = watch('modelType');
  const trainingClassToAnnotationOptions = watch('trainingClassToAnnotationOptions') || {};
  const disabledTrainingTargets = watch('disabledTrainingTargets') || [];
  const classesConfigFromModel = modelDetailsResponse?.meta?.deps?.classesConfig;

  const newClassConfig: Record<string, ClassConfigYamlSchema> = useMemo(
    () =>
      generateClassConfigFromForm({
        classesConfigFromModel,
        trainingClassToAnnotationOptions,
        disabledTrainingTargets,
      }),
    [trainingClassToAnnotationOptions, classesConfigFromModel, disabledTrainingTargets]
  );

  const filteredAndFlattenedAnnotationAssignments = useFilterAndFlattenAnnotationAssignments({
    selectedSlideIds,
    annotationAssignments,
    selectedAssignmentIds,
  });
  const availableOptions: TodoOption[] = useMemo(
    () => uniq(flatMap(filteredAndFlattenedAnnotationAssignments, 'options')),
    [filteredAndFlattenedAnnotationAssignments]
  );

  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  const customTrainingPlanText = watch('customTrainingPlanText');

  const trainingPlanPreview = `\
Request for training

Date: ${moment().format('DD/MM/YYYY')}

Project name: ${modelDetailsResponse?.meta?.projectName || 'N/A'}

Staining type:  ${stainId ? stainTypeIdToDisplayName(stainId) : 'N/A'}

Tissue type:  ${join(modelDetailsResponse?.meta?.tissueTypes, ', ') || 'N/A'}

Model type:  ${modelType || 'N/A'}

Version:  v2

Assignment IDS:  [${join(selectedAssignmentIds, ', ')}]

Study ID:  ${studyId || 'N/A'}

Baseline model description: ${modelDetailsResponse?.meta?.description || 'N/A'}

Baseline model's artifact: ${modelDetailsResponse?.url || 'N/A'}

Slides:
${join(uniq(selectedSlideIds), '\n')}

Annotations exist:  {${join(
    map(availableOptions, (option) => `'${option.name}'`),
    ', '
  )}}

Annotations to class name mapping:

${join(
  map(trainingClassToAnnotationOptions, (options, className) => `${className}: [${join(options, ', ')}]`),
  '\n'
)}

Classes config content:
${YAML.stringify(newClassConfig)}`;

  const clipboard = useClipboard({ copiedTimeout: 1000 });

  const handleCopy = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    clipboard.copy(trainingPlanPreview);
  };
  return (
    <>
      <Grid item>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Generated class config YAML</AccordionSummary>
          <AccordionDetails>
            <ClassConfigYamlPreview
              modelDetailsResponse={modelDetailsResponse}
              disabledTrainingTargets={disabledTrainingTargets}
              trainingClassToAnnotationOptions={trainingClassToAnnotationOptions}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Training Plan</AccordionSummary>
          <AccordionDetails>
            <Grid item sx={{ position: 'relative' }}>
              <Controller
                control={formMethods.control}
                name="customTrainingPlanText"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    multiline
                    fullWidth
                    minRows={4}
                    maxRows={10}
                    onChange={onChange}
                    value={!isUndefined(customTrainingPlanText) ? value : trainingPlanPreview}
                  />
                )}
              />
              <Tooltip title={clipboard.copied ? 'Copied!' : 'Click to copy'} placement="top">
                <IconButton
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                  onClick={handleCopy}
                  color="inherit"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Controller
              control={formMethods.control}
              name="customTrainingPlanText"
              render={({ field: { value } }) => (
                <Button
                  variant="text"
                  disabled={isUndefined(value) || value === trainingPlanPreview}
                  onClick={() => formMethods.setValue('customTrainingPlanText', undefined)}
                >
                  Restore default training plan text
                </Button>
              )}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
    </>
  );
};
